import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, Smartphone } from "lucide-react";


const CUSTOMER_CARE_NUMBER = "+91 7800784700";
const CUSTOMER_CARE_TEL = "tel:+917800784700";
const WHATSAPP_NUMBER_DISPLAY = "+91 9217076700";
const WHATSAPP_LINK =
  "https://wa.me/919217076700?text=" +
  encodeURIComponent("Hi ClaimPro, I need roadside assistance.");
const PLAY_STORE_LINK = "https://play.google.com/store/apps/details?id=com.claimpro.prod&hl=en";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.5, ease: "easeOut" },
});

function ActionCard({ icon, eyebrow, title, description, children, delay }) {
  return (
    <motion.div
      {...fadeUp(delay)}
      className="rounded-2xl backdrop-blur-lg bg-white/[0.04] border border-white/10
                 p-7 flex flex-col gap-4 hover:border-[#21A6E9]/50 transition-colors duration-300"
    >
      <div className="w-11 h-11 rounded-xl bg-[#21A6E9]/15 border border-[#21A6E9]/30
                      flex items-center justify-center text-[#21A6E9]">
        {icon}
      </div>

      <div>
        <div className="text-xs font-medium tracking-[0.2em] uppercase text-[#21A6E9]">
          {eyebrow}
        </div>
        <h3 className="mt-1 text-xl font-semibold text-white">{title}</h3>
        <p className="mt-2 text-sm text-slate-300 leading-relaxed">{description}</p>
      </div>

      <div className="mt-auto pt-2">{children}</div>
    </motion.div>
  );
}

export default function RequestHelp() {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen text-white overflow-hidden bg-[#020617]">
      {/* Ambient background, consistent with the hero's palette */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-[#020617] to-[#03081a]" />
        <div className="absolute -top-40 right-[-10%] w-[520px] h-[520px] rounded-full bg-[#21A6E9]/10 blur-[120px]" />
        <div className="absolute bottom-[-15%] left-[-10%] w-[420px] h-[420px] rounded-full bg-[#21A6E9]/10 blur-[120px]" />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 py-14">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* Heading */}
        <motion.div {...fadeUp(0.05)} className="mt-8 max-w-2xl">
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-[#21A6E9]">
            Request Help
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-semibold leading-[1.1] tracking-[-0.02em]">
            Reach us the way that's fastest for you.
          </h1>
          <p className="mt-4 text-slate-300 text-lg leading-relaxed">
            Call our team directly, message us on WhatsApp, or install the app to
            track your case and request a crane, recovery van, or breakdown
            support in a few taps.
          </p>
        </motion.div>

        {/* Action cards */}
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <ActionCard
            delay={0.1}
            icon={<Phone size={20} />}
            eyebrow="24/7 Customer Care"
            title="Call us now"
            description="Speak to a dispatcher directly. Fastest option if you're stranded or in an emergency."
          >
            <a
              href={CUSTOMER_CARE_TEL}
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#21A6E9]
                         py-3 font-medium shadow-lg shadow-[#21A6E9]/30 hover:shadow-[#21A6E9]/50
                         transition duration-300"
            >
              <Phone size={16} />
              {CUSTOMER_CARE_NUMBER}
            </a>
          </ActionCard>

          <ActionCard
            delay={0.18}
            icon={<MessageCircle size={20} />}
            eyebrow="WhatsApp Support"
            title="Chat with us"
            description="Send your location and vehicle details over WhatsApp — no app install needed."
          >
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#25D366]
                         py-3 font-medium text-[#04120a] shadow-lg shadow-[#25D366]/30
                         hover:shadow-[#25D366]/50 transition duration-300"
            >
              <MessageCircle size={16} />
              Chat on WhatsApp
            </a>
            <div className="mt-2 text-xs text-slate-400 text-center">
              {WHATSAPP_NUMBER_DISPLAY}
            </div>
          </ActionCard>

          <ActionCard
            delay={0.26}
            icon={<Smartphone size={20} />}
            eyebrow="ClaimPro App"
            title="Install the app"
            description="Book services, track your crane or recovery van live, and manage claims end-to-end."
          >
            <div className="flex items-center gap-4">
              <a
                href={PLAY_STORE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="h-14 w-full object-contain object-left"
                />
              </a>

              <a
                href={PLAY_STORE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 rounded-lg bg-white p-1.5"
                title="Scan to download"
              >
                <img
                   src="https://claimpro-dev.s3.ap-south-1.amazonaws.com/Assets/footer/Scanner.png"
                  alt="Scan to download the ClaimPro app on Google Play"
                  className="h-14 w-14 object-contain"
                />
              </a>
            </div>
            <p className="mt-2 text-xs text-slate-400 text-center">
              Scan the code or tap the badge — Android only, for now.
            </p>
          </ActionCard>
        </div>

        {/* Footer note */}
        <motion.p {...fadeUp(0.34)} className="mt-10 text-sm text-slate-400 text-center">
          Already have an open case? Use the app or WhatsApp to track it in real time.
        </motion.p>
      </div>
    </section>
  );
}